import { Chess, Move } from 'chess.js';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { toColor, toDests } from './ChessUtils';
import { Color } from 'chessground/types';

export class Chessviewer {
  public cg: Api;
  public chess: Chess;
  public direction: 'forward' | 'backward';
  // Dragging is off by default: on touch, grabbing a piece blocks scrolling past the board.
  private draggingEnabled = false;
  private futureMoves: Move[] = [];
  private onPlaybackHandlers: { (move: Move | null): void }[] = [];
  // Fires whenever the board changes while exploring, so the caller can re-grade the position.
  private onExploreHandlers: { (): void }[] = [];
  // Fires when dragging turns on/off, including on its own, so the toggle stays in sync.
  private onDraggableChangeHandlers: { (enabled: boolean): void }[] = [];
  // Fires when exploration starts/stops. Enabling dragging alone doesn't start it (the first dragged or
  // tapped move does), so the "Explore mode" banner follows this, not the dragging flag.
  private onExploringChangeHandlers: { (exploring: boolean): void }[] = [];
  // Practice mode: the board sits at a mistake's solve position and the player drags one move. An
  // exploration under the hood, so game navigation restores on exit, but the drag is reported for
  // grading rather than branched, and no best-move arrow is drawn.
  private practiceMode = false;
  // Bumped on each re-arm, so a pending slide-in timer from an earlier arm cancels itself.
  private practiceAnimToken = 0;
  private onPracticeMoveHandlers: { (orig: string, dest: string): void }[] = [];

  // Exploration ("what-if") mode: the board holds an isolated line from a base position; the arrows
  // can't step back past the base, and leaving restores the game move snapshotted in exploreReturn*.
  private exploring = false;
  private exploreReturnPgn: string | null = null;
  private exploreReturnFuture: Move[] = [];

  constructor(el: HTMLElement, orientation: Color) {
    const chess = new Chess();
    const cg = Chessground(el, {
      movable: {
        color: undefined,
        free: false,
        dests: toDests(chess),
      },
      highlight: {
        check: true,
      },
      draggable: {
        enabled: false,
        showGhost: true,
      },
      // View-only while dragging is off, so chessground doesn't bind the touchstart handler that
      // preventDefaults touches near a piece and blocks scrolling on mobile.
      viewOnly: true,
      orientation: orientation,
      // Arrow brushes in the move-type colours (.cm-cp-move-type.* in site.css) for the refutation arrow,
      // merged with chessground's defaults (the best-move arrow uses green).
      drawable: {
        brushes: {
          // chessground's defaults, which the type requires.
          green: { key: 'g', color: '#15781B', opacity: 1, lineWidth: 10 },
          red: { key: 'r', color: '#882020', opacity: 1, lineWidth: 10 },
          blue: { key: 'b', color: '#003088', opacity: 1, lineWidth: 10 },
          yellow: { key: 'y', color: '#e68f00', opacity: 1, lineWidth: 10 },
          // Move-type severity colours for the refutation arrow.
          scInaccuracy: { key: 'sci', color: '#e3b341', opacity: 0.9, lineWidth: 10 },
          scMistake: { key: 'scm', color: '#f0883e', opacity: 0.9, lineWidth: 10 },
          scMiss: { key: 'scms', color: '#f28b82', opacity: 0.9, lineWidth: 10 },
          scBlunder: { key: 'scb', color: '#e5533c', opacity: 0.9, lineWidth: 10 },
          // The piece hint ring (same brush as CorrectionBoard).
          hintPiece: { key: 'hintPiece', color: '#7cd3ff', opacity: 0.95, lineWidth: 12 },
        },
      },
    });
    this.cg = cg;
    this.chess = chess;
    this.direction = 'forward';
    this.updateBoard();
  }

  isDraggable(): boolean {
    return this.draggingEnabled;
  }

  isExploring(): boolean {
    return this.exploring;
  }

  isPracticing(): boolean {
    return this.practiceMode;
  }

  onPracticeMove(handler: { (orig: string, dest: string): void }) {
    this.onPracticeMoveHandlers.push(handler);
  }

  // Arm practice at the position before a mistake. movePgn is the game move to sit after (null = game
  // start). Navigating within the real game first keeps full navigation; an exploration opened from
  // there lets stopPractice() restore it exactly. The drag goes to onPracticeMove instead of branching,
  // and no best-move arrow is shown. Calling again (retry / next mistake) exits any live exploration.
  practiceFrom(movePgn: string | null) {
    if (this.exploring) {
      this.exitExploration(true);
      this.applyDraggable(false);
    }
    if (movePgn) {
      this.findPgn(movePgn);
    } else {
      while (this.previous()) {
        /* back to the game start */
      }
    }
    // The move that reached the solve position, captured before beginExploration wipes the history.
    const entering = this.getCurrentMove();
    this.beginExploration();
    this.practiceMode = true;
    this.applyDraggable(true);
    this.updateBoard();
    // Show how the position arose: flash to before that move, hold a beat, then let chessground slide it
    // in. A generation token cancels a pending slide if practice re-arms first.
    if (entering != null && entering.before) {
      const cg = this.cg;
      const { before, after, from, to } = entering;
      cg.set({ animation: { enabled: false }, fen: before, lastMove: undefined });
      const token = ++this.practiceAnimToken;
      setTimeout(() => {
        if (token !== this.practiceAnimToken || !this.practiceMode) {
          return;
        }
        cg.set({ animation: { enabled: true }, fen: after, lastMove: [from, to] });
      }, 900);
    }
  }

  // Leave practice and restore the solve position. The caller then navigates to the mistake move.
  stopPractice() {
    this.practiceMode = false;
    if (this.exploring) {
      this.exitExploration(true);
      this.applyDraggable(false);
      this.updateBoard();
    }
  }

  // Turning dragging on enters exploration from the current position (graded right away); turning it
  // off leaves exploration and snaps back to the game move.
  setDraggable(enabled: boolean) {
    if (enabled === this.draggingEnabled) {
      return;
    }
    if (enabled) {
      this.beginExploration();
      this.applyDraggable(true);
      this.onExploreHandlers.forEach((h) => h());
    } else {
      this.exitExploration(true);
      this.applyDraggable(false);
      this.onPlaybackHandlers.forEach((h) => h(this.getCurrentMove()));
    }
  }

  // Apply the dragging flag and rebind handlers, without setDraggable's enter/exit logic.
  private applyDraggable(enabled: boolean) {
    this.draggingEnabled = enabled;
    // View-only when off (see the constructor).
    this.cg.set({ draggable: { enabled }, viewOnly: !enabled });
    // redrawAll rebinds the board handlers but rebinds the DOCUMENT drag listeners only when
    // dom.unbind is empty. A board that started view-only never bound them, so a piece could be picked up
    // but not moved. Unbind and clear first.
    const dom = this.cg.state.dom;
    if (dom?.unbind) {
      dom.unbind();
      dom.unbind = undefined;
    }
    this.cg.redrawAll();
    this.updateBoard();
    this.onDraggableChangeHandlers.forEach((h) => h(enabled));
  }

  onExplore(handler: { (): void }) {
    this.onExploreHandlers.push(handler);
  }
  onPlayback(handler: { (move: Move | null): void }) {
    this.onPlaybackHandlers.push(handler);
  }
  onDraggableChange(handler: { (enabled: boolean): void }) {
    this.onDraggableChangeHandlers.push(handler);
  }
  onExploringChange(handler: { (exploring: boolean): void }) {
    this.onExploringChangeHandlers.push(handler);
  }

  loadPgn(pgn: string) {
    this.chess.loadPgn(pgn);
    // Leftover futureMoves belong to the previous game and would replay into an illegal position.
    this.futureMoves = [];
    this.direction = 'forward';
    this.exploring = false;
    this.practiceMode = false;
    this.exploreReturnPgn = null;
    this.exploreReturnFuture = [];
    this.updateBoard();
  }

  // Snapshot the game position and reset to a base position with an empty history, seeding an
  // exploration line. baseFen defaults to the current board.
  private beginExploration(baseFen?: string) {
    if (!this.exploring) {
      this.exploreReturnPgn = this.chess.pgn();
      this.exploreReturnFuture = [...this.futureMoves];
      this.exploring = true;
      this.onExploringChangeHandlers.forEach((h) => h(true));
    }
    const fen = baseFen ?? this.chess.fen();
    try {
      this.chess.load(fen);
    } catch {
      /* keep the current position if the fen is somehow unusable */
    }
    this.futureMoves = [];
    this.direction = 'forward';
  }

  // Leave exploration. With restore the board returns to the game position it started from.
  private exitExploration(restore: boolean) {
    if (!this.exploring) {
      return;
    }
    if (restore) {
      try {
        if (this.exploreReturnPgn) {
          this.chess.loadPgn(this.exploreReturnPgn);
        } else {
          this.chess.reset();
        }
      } catch {
        this.chess.reset();
      }
      this.futureMoves = this.exploreReturnFuture;
    }
    this.exploring = false;
    // Leaving the line always leaves practice.
    this.practiceMode = false;
    this.exploreReturnPgn = null;
    this.exploreReturnFuture = [];
    this.direction = 'forward';
    this.onExploringChangeHandlers.forEach((h) => h(false));
  }

  // Load an engine line (UCI) onto the board after the move at stopIndex, with the rest queued for the
  // > arrow. Turns dragging on. Used when a best-line move is tapped.
  exploreLine(baseFen: string, uciMoves: string[], stopIndex: number) {
    this.beginExploration(baseFen);
    for (const uci of uciMoves) {
      if (uci.length < 4) {
        break;
      }
      try {
        this.chess.move({
          from: uci.slice(0, 2),
          to: uci.slice(2, 4),
          promotion: uci.length > 4 ? uci[4] : undefined,
        });
      } catch {
        break;
      }
    }
    // Step back to the tapped move; undone moves land in futureMoves for the > arrow.
    while (this.chess.history().length - 1 > stopIndex) {
      this.previous();
    }
    this.direction = 'forward';
    if (!this.draggingEnabled) {
      this.applyDraggable(true);
    } else {
      this.updateBoard();
    }
    this.onExploreHandlers.forEach((h) => h());
  }

  getCurrentMove(): Move | null {
    return this.chess.history({ verbose: true }).at(-1) ?? null;
  }

  getNextMove(): Move | null {
    return this.futureMoves.at(-1) ?? null;
  }

  getPreviousMove(): Move | null {
    return this.chess.history({ verbose: true }).at(-2) ?? null;
  }

  getFirstMove(): Move | null {
    return this.chess.history({ verbose: true }).at(0) ?? null;
  }

  flip() {
    this.cg.toggleOrientation();
  }

  private updateBoard() {
    const current = this.getCurrentMove();
    this.cg.set({
      fen: this.chess.fen(),
      check: this.chess.isCheck(),
      turnColor: toColor(this.chess),
      lastMove: current != null ? [current.from, current.to] : [],
      movable: {
        events: {
          after: (orig: string, dest: string) => {
            // Practice: the drop is the answer. Apply it, lock dragging (one attempt) and hand it to the grader,
            // staying in exploration so stopPractice() can still restore the game.
            if (this.practiceMode) {
              try {
                this.chess.move({ from: orig, to: dest, promotion: 'q' });
              } catch {
                this.updateBoard();
                return;
              }
              // The graded attempt has landed. Leave practice but stay in exploration with dragging on, so the
              // player can keep playing the line without re-arming.
              this.practiceMode = false;
              this.updateBoard();
              this.onPracticeMoveHandlers.forEach((h) => h(orig, dest));
              return;
            }
            // The first dragged move enters exploration: snapshot the game position, then apply the move.
            const wasExploring = this.exploring;
            if (!wasExploring) {
              this.beginExploration();
            }
            try {
              this.chess.move({ from: orig, to: dest, promotion: 'q' });
            } catch {
              // Illegal drop: undo the just-started exploration.
              if (!wasExploring) {
                this.exitExploration(true);
              }
              this.updateBoard();
              return;
            }
            this.updateBoard();
            this.onExploreHandlers.forEach((h) => h());
          },
        },
        color: this.draggingEnabled ? toColor(this.chess) : undefined,
        dests: toDests(this.chess),
      },
    });
  }

  private previous = (): Move | null => {
    const lastMove = this.getCurrentMove();
    if (lastMove) {
      this.futureMoves.push(lastMove);
    }
    this.direction = 'backward';
    return this.chess.undo();
  };
  private next = (): Move | null => {
    const next = this.futureMoves.pop();
    if (next) {
      try {
        this.chess.move(next);
      } catch {
        // The queued move doesn't belong to this position; drop the rest rather than crash.
        this.futureMoves = [];
        return null;
      }
    }
    this.direction = 'forward';
    return next ?? null;
  };

  private findPgn = (pgn: string): Move | null => {
    // chess.js (beta.8) drops SAN disambiguation on some moves (issue #87): Nce7 becomes Ne7. Round-trip
    // the target through chess.js so both sides use its SAN generator.
    try {
      const norm = new Chess();
      norm.loadPgn(pgn);
      pgn = norm.pgn();
    } catch {
      /* not a full pgn we can normalize; fall back to the raw string */
    }
    while (this.previous()) {
      if (this.chess.pgn() == pgn) {
        this.direction = 'forward';
        return this.getCurrentMove();
      }
    }
    while (this.next()) {
      if (this.chess.pgn() == pgn) {
        return this.getCurrentMove();
      }
    }
    return null;
  };

  go(direction: 'next' | 'previous' | 'back' | 'last' | 'first' | string) {
    if (this.exploring) {
      if (direction == 'previous' || direction == 'prev' || direction == 'back') {
        this.previous();
      } else if (direction == 'next') {
        this.next();
      } else if (direction == 'first') {
        while (this.previous()) {
          /* back to the base of the exploration line */
        }
      } else if (direction == 'last') {
        while (this.next()) {
          /* forward to the end of the exploration line */
        }
      } else {
        // A pgn is a game-move selection: leave exploration and navigate the real game to it.
        this.exitExploration(true);
        this.applyDraggable(false);
        this.findPgn(direction);
        this.updateBoard();
        this.onPlaybackHandlers.forEach((h) => h(this.getCurrentMove()));
        return;
      }
      this.updateBoard();
      this.onExploreHandlers.forEach((h) => h());
      return;
    }

    if (direction == 'previous' || direction == 'prev' || direction == 'back') {
      this.previous();
    } else if (direction == 'next') {
      this.next();
    } else if (direction == 'first') {
      while (this.previous()) {
        /* empty */
      }
    } else if (direction == 'last') {
      while (this.next()) {
        /* empty */
      }
    } else {
      this.findPgn(direction);
    }
    this.updateBoard();
    const current = this.getCurrentMove();
    this.onPlaybackHandlers.forEach((h) => h(current));
  }
}
